import { useCallback, useContext } from 'react';
import { PermissionContext } from '../contexts/PermissionContext';

function normalize(value) {
  return String(value ?? '').trim().toLowerCase();
}

function moduleKeysMatch(a, b) {
  const left = normalize(a);
  const right = normalize(b);
  if (!left || !right) return false;
  return left === right || `${left}s` === right || `${right}s` === left;
}

function findModule(all, moduleName) {
  if (!all || typeof all !== 'object' || Array.isArray(all)) return null;
  const key = Object.keys(all).find((candidate) => moduleKeysMatch(candidate, moduleName));
  return key ? all[key] : null;
}

function permissionScalar(value) {
  if (value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'value')) {
    return value.value;
  }
  return value;
}

function permissionAllowed(value, missingDefault = false) {
  const scalar = permissionScalar(value);
  if (scalar === undefined || scalar === null) return missingDefault;
  return scalar === true || scalar === 1 || scalar === '1'
    || String(scalar).trim().toLowerCase() === 'true';
}

function findAction(actions, action) {
  if (!actions || typeof actions !== 'object') return undefined;
  if (Object.prototype.hasOwnProperty.call(actions, action)) return actions[action];
  const wanted = normalize(action);
  const key = Object.keys(actions).find((candidate) => normalize(candidate) === wanted);
  return key ? actions[key] : undefined;
}

function storedPermission(moduleName, action) {
  try {
    const all = JSON.parse(localStorage.getItem('menuPermission') || '{}');
    const modulePermissions = findModule(all, moduleName);
    // Preserve the old roleAllowsAction behaviour for deployments/screens that
    // have not mounted PermissionProvider yet: an unconfigured explicit action
    // was historically treated as allowed.
    return permissionAllowed(findAction(modulePermissions, action), true);
  } catch {
    return true;
  }
}

// can(module, action) — the single gate for role-based UI visibility.
// Live /me/permissions data is authoritative. The localStorage fallback is only
// for initial loading/legacy standalone consumers and prevents a stale login
// snapshot from becoming the long-term source of truth.
export function usePermissions() {
  const { permissions, loading } = useContext(PermissionContext);

  const can = useCallback((moduleName, action) => {
    if (permissions?.['*']?.['*']) return true;

    const modulePermissions = findModule(permissions, moduleName);
    if (modulePermissions) {
      return permissionAllowed(findAction(modulePermissions, action), false);
    }

    if (loading || Object.keys(permissions ?? {}).length === 0) {
      return storedPermission(moduleName, action);
    }

    // If the live role matrix loaded successfully but has no entry for this
    // module, retain compatibility with the old login map rather than hiding a
    // dynamically configured action solely because that module has not yet been
    // migrated into role_menu_permissions.
    return storedPermission(moduleName, action);
  }, [permissions, loading]);

  return { can, permissions, loading };
}
